import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useContext,
} from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Menu, Dropdown, Space, Tooltip, Modal } from "antd";
import {
  MenuOutlined,
  CopyOutlined,
  ImportOutlined,
  SnippetsOutlined,
} from "@ant-design/icons";
//import helpers
import {
  ACTION_TYPE_DETAILED_VIEW,
  AUDIT_TRAIL_BULK_ACTIONS,
  DETAILS_MENU_FORMATTED,
} from "constants/auditTrail";

import AdditionalDataModal from "./AdditionalData";
import EditTradeDetails from "components/EditTradeDetails/EditTradeDetails";

//import Style
import "styles/PricerAuditAgGridLayout.scss";
import { GlobalContext } from "contexts/GlobalContext";
import {
  extractCopyShortHandForSummaryRows,
  isSummaryLevel,
  resolveRowsForMenuAction,
} from "helpers/generalHelper";
import { ACTION_BUTTONS_NAME } from "constants/generalActionButtons";
import { MarketAnalysisContext } from "contexts/MarketAnalysisContext";
import { handleCopyShortHand, handleCopyTradeDetails, toast } from "helpers/generalHelper";
import { deleteAuditTrailRow } from "api/auditTrailApi";
import { createPortal } from "react-dom";
import {
  trackAuditTrailMenuKey,
  trackAuditTrailVoidSuccess,
} from "helpers/auditTrailAnalytics";
import { handlePullToPricerClick } from "helpers/auditTrailHelper";
import { AuditTrailContext } from "contexts/AuditTrailContext";
import { FIELD_NAMES } from "constants/auditTrail";

const AuditTrailActionButtons = (props) => {
  const {
    globalState: { auditTrailState, dark_Theme },
  } = useContext(GlobalContext);
  const params = useParams();
  const location = useLocation();

  const { MarketAnalysisState } = useContext(MarketAnalysisContext);
  const {
    state: auditTrailContextState,
    setTradePopupData,
    setContextMenuAction,
  } = useContext(AuditTrailContext);
  const [detailedMenu, setDetailedMenu] = useState();
  const navigate = useNavigate();
  const selectedRows = location.pathname.includes("market-analysis")
    ? MarketAnalysisState.selectedRows
    : auditTrailState.selectedRows;
  const [menuItemClicked, setMenuItemClicked] = useState({
    [ACTION_BUTTONS_NAME.COPY_SHORTHAND.key]: false,
    [ACTION_BUTTONS_NAME.COPY_TRADE_DETAILS.key]: false,
    [ACTION_BUTTONS_NAME.COPY_SHORTHAND_LEGS.key]: false,
    [ACTION_BUTTONS_NAME.PULL_TO_PRICER.key]: false,
    [ACTION_BUTTONS_NAME.ADDITIONAL_DATA.key]: false,
    [ACTION_BUTTONS_NAME.DETAILED_AUDIT_TRIAL.key]: false,
    [ACTION_BUTTONS_NAME.EDIT_TRADE_DETAILS.key]: false,
    [ACTION_BUTTONS_NAME.VOID.key]: false,
  });
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  useEffect(() => {
    let newMenu = [];
    let detailedMenuData = DETAILS_MENU_FORMATTED;

    if (params?.id) {
      detailedMenuData.map((menu) => {
        if (menu.key !== "detailed_audit_trail") {
          newMenu.push(menu);
        }
        return menu;
      });
    } else {
      newMenu = detailedMenuData;
    }
    // it was readding the menu items on every render and it was causing the menu to be duplicated
    if (!props.data?.can_be_voided) {
      newMenu = newMenu.filter(
        (menu) => menu.key !== ACTION_BUTTONS_NAME.VOID.key
      );
    }

    const rowData = props.data ? props.data : props.node?.aggData;
    const isTradeAction = rowData?.[FIELD_NAMES.ACTION] === "Trade";
    newMenu = newMenu.filter((m) => m.key !== "filter_matches");
    if (isTradeAction) {
      newMenu.push({
        key: "filter_matches",
        label: "Clearing",
        icon: <SnippetsOutlined />,
      });
    }

    setDetailedMenu(newMenu);
  }, [props.data, props.node?.aggData, params]);

  const getAuditTrailPage = useCallback(() => {
    if (location.pathname.includes("market-analysis")) return "market_analysis";
    if (params?.id) return "audit_trail_detail";
    return "audit_trail";
  }, [location.pathname, params?.id]);

  // Bulk is only offered when the row the menu was opened from is part of the selection.
  const showBulkMenu = useMemo(() => {
    const rowData = props.data ? props.data : props.node?.aggData;
    return selectedRows.length >= 2 && selectedRows.includes(rowData?.summary_id);
  }, [props.data, props.node?.aggData, selectedRows]);

  // Handle actions triggered from the dropdown / icon menu.
  // Reset the active key immediately so re-renders don't replay the same action.
  useEffect(() => {
    const activeKey = Object.keys(menuItemClicked).find((k) => menuItemClicked[k]);
    if (!activeKey) return;

    // Modal actions must stay active until the modal closes itself.
    // Auto-resetting them here causes "opens sometimes" race conditions.
    const modalControlledKeys = [
      ACTION_BUTTONS_NAME.ADDITIONAL_DATA.key,
      ACTION_BUTTONS_NAME.EDIT_TRADE_DETAILS.key,
    ];
    if (!modalControlledKeys.includes(activeKey)) {
      setMenuItemClicked((prev) => ({ ...prev, [activeKey]: false }));
    }

    const data = props.data ? props.data : props.node.aggData;

    if (activeKey === ACTION_BUTTONS_NAME.COPY_SHORTHAND.key) {
      extractCopyShortHandForSummaryRows(props, true, false, params?.id ? true : false);
      return;
    }

    if (activeKey === ACTION_BUTTONS_NAME.COPY_SHORTHAND_LEGS.key) {
      extractCopyShortHandForSummaryRows(props, true, true, params?.id ? true : false);
      return;
    }

    if (activeKey === ACTION_BUTTONS_NAME.COPY_SHORTHAND_NO_VOLS.key) {
      extractCopyShortHandForSummaryRows(props, false, false, params?.id ? true : false);
      return;
    }

    if (activeKey === ACTION_BUTTONS_NAME.PULL_TO_PRICER.key) {
      handlePullToPricerClick(
        resolveRowsForMenuAction(data?.summary_id, selectedRows)
      );
      return;
    }

    if (activeKey === ACTION_BUTTONS_NAME.TRADE.key) {
      setTradePopupData({ isTradeItemClicked: true, data: props.data });
      return;
    }

    if (activeKey === ACTION_BUTTONS_NAME.COPY_TRADE_DETAILS.key) {
      handleCopyTradeDetails(data?.trade_details);
      return;
    }

    if (activeKey === ACTION_BUTTONS_NAME.VOID.key) {
      setShowVoidModal(true);
      return;
    }

    if (activeKey === ACTION_BUTTONS_NAME.DETAILED_AUDIT_TRIAL.key) {
      navigate(`/audit-trail/${data?.["team_run_row_id"]}`);
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemClicked]);

  const onVoidConfirmed = () => {
    const data = props.data ? props.data : props.node.aggData;
    const auditTrailId = data.audit_trail_id;
    setConfirmLoading(true);
    deleteAuditTrailRow(auditTrailId).then((res) => {
      refreshDataSource(props.api);
      toast(res?.data?.message, "success");
      trackAuditTrailVoidSuccess({ page: getAuditTrailPage() });
      setConfirmLoading(false);
      setShowVoidModal(false);
    });
  };

  const refreshDataSource = (gridOptions) => {
    const currentPage = props.api.paginationGetCurrentPage();
    // Refresh the server-side data source
    props.api.refreshServerSideStore();
    // Navigate back to the same page after refresh
    props.api.paginationGoToPage(currentPage);
  };

  const handleMenuItemClicked = useCallback((key, value) => {
    setMenuItemClicked((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Keep a ref to the latest props so the context-menu effect doesn't need props
  // in its dependency array (props changes on every AG Grid re-render and would
  // cause the effect to fire multiple times while contextMenuAction is still set).
  const propsRef = useRef(props);
  propsRef.current = props;

  // Handle actions triggered from AG Grid context menu (right-click on row)
  useEffect(() => {
    const contextMenuAction = auditTrailContextState?.contextMenuAction;
    if (!contextMenuAction || !contextMenuAction.menuKey) return;

    const currentProps = propsRef.current;
    const data = currentProps.node?.data ?? currentProps.node?.aggData ?? currentProps.data;
    if (!data) return;

    const myId = data[FIELD_NAMES.AUDIT_TRAIL_ID];
    if (myId == null || String(contextMenuAction.auditTrailId) !== String(myId)) return;

    const page = getAuditTrailPage();

    if (contextMenuAction.menuKey === "filter_matches") {
      return;
    }

    // Right-click Void is owned by AgGridTable (one confirm modal). Handling it
    // here as well stacked a second popup behind the first.
    if (contextMenuAction.menuKey === ACTION_BUTTONS_NAME.VOID.key) {
      return;
    }

    trackAuditTrailMenuKey(contextMenuAction.menuKey, { source: "context_menu", page });
    handleMenuItemClicked(contextMenuAction.menuKey, true);
    setContextMenuAction(null);
  // propsRef is a ref — intentionally excluded; it always holds the latest props.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auditTrailContextState?.contextMenuAction,
    handleMenuItemClicked,
    getAuditTrailPage,
    setContextMenuAction,
  ]);

  const showSelectedItem = useMemo(() => {
    if (menuItemClicked[ACTION_BUTTONS_NAME.ADDITIONAL_DATA.key]) {
      return (
        <AdditionalDataModal
          isOpen={menuItemClicked[ACTION_BUTTONS_NAME.ADDITIONAL_DATA.key]}
          handleMenuItemClicked={handleMenuItemClicked}
          rowSelectedInfo={props?.data ? props.data : props.node.aggData}
        />
      );
    }

    if (menuItemClicked[ACTION_BUTTONS_NAME.EDIT_TRADE_DETAILS.key]) {
      return (
        <EditTradeDetails
          isEditMode
          isOpen={menuItemClicked[ACTION_BUTTONS_NAME.EDIT_TRADE_DETAILS.key]}
          handleMenuItemClicked={handleMenuItemClicked}
          rowSelectedInfo={props?.data ? props.data : props.node.aggData}
        />
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItemClicked]);

  const showCopyShortHandAndPullPricer = useMemo(() => {
    if (!params.id) return;
    if (props.data.action !== ACTION_TYPE_DETAILED_VIEW.ARCHIVE) return;

    return (
      <>
        <Tooltip title={ACTION_BUTTONS_NAME.COPY_SHORTHAND.label}>
          <CopyOutlined
            className="button-icon"
            onClick={() => {
              trackAuditTrailMenuKey(ACTION_BUTTONS_NAME.COPY_SHORTHAND.key, {
                source: "row_icon",
                page: "audit_trail_detail",
              });
              handleCopyShortHand(props.data?.copy_shorthand);
            }}
          />
        </Tooltip>
        <Tooltip title={ACTION_BUTTONS_NAME.PULL_TO_PRICER.label}>
          <ImportOutlined
            className="rotate-import--icon button-icon"
            onClick={() => {
              trackAuditTrailMenuKey(ACTION_BUTTONS_NAME.PULL_TO_PRICER.key, {
                source: "row_icon",
                page: "audit_trail_detail",
              });
              handlePullToPricerClick(props.data?.summary_id);
            }}
          />
        </Tooltip>
      </>
    );
  }, [props.data, params?.id]);

  const showCopyDetailsAndEditTradeDetails = useMemo(() => {
    if (!params.id) return;
    if (props.data.action !== ACTION_TYPE_DETAILED_VIEW.TRADE) return;
    return (
      <>
        <Tooltip title={ACTION_BUTTONS_NAME.COPY_TRADE_DETAILS.label}>
          <SnippetsOutlined
            onClick={() => {
              trackAuditTrailMenuKey(ACTION_BUTTONS_NAME.COPY_TRADE_DETAILS.key, {
                source: "row_icon",
                page: "audit_trail_detail",
              });
              handleCopyTradeDetails(props.data?.copy_trade_details);
            }}
            className="button-icon"
          />
        </Tooltip>
      </>
    );
  }, [props.data, params?.id]);

  if (!isSummaryLevel(props)) return <></>;

  return (
    <>
      <div className="action-buttons__container action-button_container">
        {/* Show Modal */}
        {showSelectedItem}
        {/* End Of Show Modal Func */}
        {/* Action Buttons With Tooltip */}
        {showCopyShortHandAndPullPricer}
        {showCopyDetailsAndEditTradeDetails}

        <Dropdown
          className={`action-buttons__dropdown button-icon ${
            !dark_Theme && "button-light-theme"
          }`}
          key="action-buttons__menu-items"
          trigger={["click"]}
          overlayClassName={`audit-trail-action-column-dropdown ${
            dark_Theme ? "menu-dropdown-dark" : ""
          }`}
          overlay={
            <Menu
              id="audit-trail-action-column-menu"
              onClick={({ key }) => {
                const page = getAuditTrailPage();
                if (key === "filter_matches") {
                  trackAuditTrailMenuKey("filter_matches", {
                    source: "dropdown",
                    page,
                  });
                  const data = props.data ? props.data : props.node?.aggData;
                  const auditTrailId = data?.[FIELD_NAMES.AUDIT_TRAIL_ID];
                  if (!auditTrailId) return;
                  setContextMenuAction({
                    auditTrailId: String(auditTrailId),
                    menuKey: "filter_matches",
                    rowData: data,
                  });
                  return;
                }
                trackAuditTrailMenuKey(key, { source: "dropdown", page });
                return handleMenuItemClicked(key, true);
              }}
              key="action-buttons__menu-items_MENU"
              items={showBulkMenu ? AUDIT_TRAIL_BULK_ACTIONS : detailedMenu}
              theme={dark_Theme ? "dark" : "light"}
            />
          }
        >
          <Space>
            <MenuOutlined id="audit-trail-menu-items" />
          </Space>
        </Dropdown>
        {createPortal(
          <Modal
            className={`${dark_Theme && "ant-modal-dark content_dark"}`}
            title="Void Confirmation"
            style={{
              top: 20,
            }}
            confirmLoading={confirmLoading}
            open={showVoidModal}
            okText="Void"
            onOk={() => onVoidConfirmed()}
            onCancel={() => setShowVoidModal(false)}
            centered
          >
            <p>
              Are you sure you want to void this item? This action cannot be
              undone.
            </p>
          </Modal>,
          document.body
        )}
        {/* End Of Action Buttons With Tooltip */}
      </div>
    </>
  );
};

export default AuditTrailActionButtons;
